import { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { Spin } from 'antd';

import _, { debounce } from 'lodash';
import Down from 'assets/svg/Down';

import InfiniteScroller from '../molecules/InfiniteScroller';
import { UserContext } from 'contexts/ConfigContext';
import { deleteTypeNameFromResponse, fetchScopeID, getConfig } from 'utils/helperMethods';
import { client } from 'services/apolloInit';
import { applyFilterMutation } from 'gqlQueries/filterQueries';
import FilterAttrNameList from 'components/atoms/Filter/FilterAttrNameList';
import useFetchAttributeNamesOnly from 'hooks/Filter/useFetchAttributeNamesOnly';
import { useOnClickOutside } from 'hooks/general/useOnOutsideClick';
import { json } from 'stream/consumers';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PROMO_SCORECARD_SELECT_VARIABLE_DROPDOWN_CHANGED,
    PROMO_SCORECARD_SELECT_VARIABLE_DROPDOWN_SEARCHED,
} from 'utils/mixpanelEventNames';
interface Props {
    attributeType: any;
    filterID: any;
    setSelectionId: any;
    setLoadingStateForFilterCall: Function;
    checkedAttributeNames: any;
    setCheckAttributeNames: Function;
    checkedAttributeNamesRef: any;
    setLoadingStateForAttrNames: Function;
    style?: any;
    screenFilterConfig: any;
}

export const MultiSelectionAttribute: React.FC<Props> = ({
    attributeType,
    filterID,
    setSelectionId,
    setLoadingStateForFilterCall,
    checkedAttributeNames,
    setCheckAttributeNames,
    checkedAttributeNamesRef,
    setLoadingStateForAttrNames,
    screenFilterConfig,
    style,
}) => {
    const [listFlag, setList] = useState(false);
    const refForOnlyInternalshowList = useRef(false);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const filterConfig = getConfig('globalFilter', appConfig);
    const offsetRef = useRef(commonConfig.offset || 0);
    const [searchKey, setSearchKey] = useState('');
    const outsideClickRef = useRef<any>(null);
    const [attributeNamesTOShow, setAttributeNamesTOShow] = useState<any>([]);

    let memoisedFilterIdFroInfiniteScroll = useRef<any>();

    const heightAccumulator = useRef(0);
    const currentScreenFeatureName = screenFilterConfig?.dimensionListKey || 'default';

    let {
        fetchAttributeNamesOnly,
        data: attributeNames,
        loading: attributeNamesLoading,
        error: attributeNamesError,
        count,
    } = useFetchAttributeNamesOnly();

    function wrapperFetchAttributeNamesOnly(search = '') {
        fetchAttributeNamesOnly({
            variables: {
                attributeType: attributeType,
                dimension: 'dim1',
                filters: JSON.stringify(filterID),
                isAttribute: true,
                featureName: currentScreenFeatureName,
                limit: 50,
                offset: 0,
                searchKeyword: search,
                rbacFilters: fetchScopeID(filterConfig.defaultIdObject),
            },
        });
    }

    useEffect(() => {
        setLoadingStateForAttrNames(attributeNamesLoading);
    }, [attributeNamesLoading]);

    useEffect(() => {
        if (filterID.dim1.ids.length || filterID.dim2.ids.length) {
            wrapperFetchAttributeNamesOnly();
        }
        setSearchKey('');
    }, [attributeType, filterID]);

    //show list function
    const showList = () => {
        setList(!listFlag);
        refForOnlyInternalshowList.current = !refForOnlyInternalshowList.current;
    };

    //calculate the height for infinite scroll
    const calculateHeight = () => {
        const factor = offsetRef.current ? Math.floor(offsetRef.current / 100) + 1 : 1;
        const height = factor * 1500;
        return height;
    };

    //will trigger when to set the scroll for infinite scroll
    useEffect(() => {
        //When the user infinite scrolls,scroll to the latest position instead of scrolling to the top on each render
        if (attributeNames?.length && offsetRef.current > 0) {
            const elem = Array.from(document.getElementsByClassName('infinite-scroll-component'));
            if (elem[0]) {
                const currentHeight = calculateHeight();

                elem[0].scrollTo(0, currentHeight - heightAccumulator.current);
            }
        }
        if (attributeNames?.length) {
            setAttributeNamesTOShow(() => sortListOfAttributeNames(attributeNames));
        }
    }, [attributeNames]);

    //call apply filter
    function callApplyFilter() {
        let filter: any = { jsonstring_dim1: {}, jsonstring_dim2: '{}', featureName: currentScreenFeatureName };
        let inclusion = [];
        for (let keys in checkedAttributeNamesRef.current) {
            inclusion.push(checkedAttributeNamesRef.current[keys]);
        }
        let temp: any = { inclusion };
        if (!temp.inclusion.length) {
            delete temp.inclusion;
        }

        filter.jsonstring_dim1 = JSON.stringify(temp);

        setLoadingStateForFilterCall(true);
        try {
            client
                .mutate({
                    mutation: applyFilterMutation,
                    variables: filter,
                    fetchPolicy: 'no-cache',
                })
                .then((payload: any) => {
                    deleteTypeNameFromResponse(payload?.data);
                    setLoadingStateForFilterCall(false);
                    setSelectionId({ ...payload.data });
                });
        } catch (error) {
            setLoadingStateForFilterCall(false);
        }
    }

    //close expanded filter on clicking outside
    const clickOutsideHandler = useCallback(
        (event: any) => {
            if (listFlag) {
                setList(false);
                callApplyFilter();
                setAttributeNamesTOShow(() => sortListOfAttributeNames(attributeNames));
            }
        },
        [listFlag],
    );
    useOnClickOutside(outsideClickRef, clickOutsideHandler);

    function sortListOfAttributeNames(attributeNames: any) {
        let checkedList: any = [];
        let checkKeys: any = [];
        for (let checkKey in checkedAttributeNamesRef.current) {
            checkedList.push(checkedAttributeNamesRef.current[checkKey]);
            checkKeys.push(checkKey);
        }

        let temp: any = [];
        attributeNames?.forEach((item: any) => {
            if (!checkKeys.includes(item.attributeExId)) {
                temp.push(item);
            }
        });

        return [...checkedList, ...temp];
    }

    //onclick of dropdown
    let onClickOfDropdown = (listFlag: any) => {
        if (listFlag) {
            callApplyFilter();
            setAttributeNamesTOShow(() => sortListOfAttributeNames(attributeNames));
        }
        showList();
    };

    //onclick of checkbox of attribute name
    const onClickCheckbox = (checkboxItem: any, e: any) => {
        let temp = { ...checkedAttributeNames };
        if (e.target.checked) {
            if (Object.keys(checkedAttributeNames).length > 10) return;
            temp[checkboxItem.attributeExId] = checkboxItem;
            checkedAttributeNamesRef.current = temp;
            setCheckAttributeNames(() => temp);
        } else {
            delete temp[checkboxItem.attributeExId];
            checkedAttributeNamesRef.current = temp;

            setCheckAttributeNames(() => temp);
        }
        trackEventMixpanel(PROMO_SCORECARD_SELECT_VARIABLE_DROPDOWN_CHANGED, {
            dropdownName: checkboxItem?.attributeExId,
            isChecked: e.target.checked,
        });
    };

    //trigger by the infinite scroll component
    const infiniteScrollOnNext = (loading: boolean) => {
        if (!loading) {
            const newOffset = offsetRef.current + filterConfig.limit;
            let filter = JSON.stringify(memoisedFilterIdFroInfiniteScroll.current);

            fetchAttributeNamesOnly({
                variables: {
                    attributeType: attributeType,
                    dimension: 'dim1',
                    filters: JSON.stringify(filterID),
                    isAttribute: true,
                    featureName: currentScreenFeatureName,
                    limit: filterConfig.limit,
                    offset: newOffset,
                    searchKeyword: '',
                    rbacFilters: fetchScopeID(filterConfig.defaultIdObject),
                },
            });

            offsetRef.current = newOffset;
        }
    };

    //memo debounce function
    const deBounce = useCallback(debounce(wrapperFetchAttributeNamesOnly, 300), [attributeType, filterID]);

    //onchange of search value
    function onSearch(e: any) {
        trackEventMixpanel(PROMO_SCORECARD_SELECT_VARIABLE_DROPDOWN_SEARCHED, {
            searchText: e.target.value,
        });
        if (!listFlag) {
            setList(!listFlag);
        }
        setSearchKey(e.target.value);
        deBounce(e.target.value);
    }
    function placeholder() {
        if (Object.keys(checkedAttributeNames).length) {
            return 'Multi Selection';
        } else {
            return 'Select Variable';
        }
    }
    return (
        <div className="inline-block relative" ref={outsideClickRef}>
            <div className="dropdown-height px-2 rounded border border-purpleFont dropdown-width-multi-select flex items-center gap-1 justify-between">
                <input
                    style={{ whiteSpace: 'nowrap', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    type="text"
                    className="bg-white font-xs focus:outline-none placeholder-black"
                    onChange={(e) => onSearch(e)}
                    placeholder={placeholder()}
                    value={searchKey}
                />
                <span
                    className="z-10 font-normal  text-center text-slate-300 text-base w-4"
                    onClick={() => onClickOfDropdown(listFlag)}
                >
                    <Down />
                </span>
            </div>
            <div
                className={`bg-white dropdown-menu absolute z-50 text-primary w-full shadow-l drop-shadow-lg rounded-b ${
                    listFlag ? `` : `hidden`
                }`}
            >
                {attributeNamesLoading ? (
                    <div className="h-32 flex justify-center items-center">
                        <Spin />
                    </div>
                ) : (
                    <InfiniteScroller
                        data={attributeNamesTOShow}
                        loading={attributeNamesLoading}
                        hasMore={attributeNamesTOShow?.length < count}
                        height={128}
                        onNext={() => infiniteScrollOnNext(attributeNamesLoading)}
                    >
                        <ul>
                            {attributeNamesTOShow?.length ? (
                                ''
                            ) : (
                                <div className="flex justify-center items-center h-full w-full mt-6">Not Found</div>
                            )}
                            {attributeNamesTOShow?.map((item: any, index: number) => {
                                return (
                                    <li key={index} className={'mx-3 mt-2'}>
                                        <div className="text-base flex gap-2 items-center cursor-pointer" key={index}>
                                            <input
                                                type={'checkbox'}
                                                className={'checkbox-size accent-purpleFont'}
                                                value={
                                                    checkedAttributeNames &&
                                                    item?.attributeExId in checkedAttributeNames
                                                }
                                                checked={
                                                    checkedAttributeNames
                                                        ? item?.attributeExId in checkedAttributeNames
                                                        : false
                                                }
                                                onChange={(e) => {
                                                    onClickCheckbox(item, e);
                                                }}
                                                disabled={!item?.isActive}
                                            />
                                            <span style={{ wordBreak: 'break-all' }} className="font-xs">
                                                {item?.attributeExId}
                                            </span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </InfiniteScroller>
                )}
            </div>
        </div>
    );
};
